// The frame every page of the sales pipeline shares: the section's title, the tab row (Today · Pipeline · Funnel) and the
// page's own actions, so the pages read as one section. A page is `<LeadsHeader current="pipeline" actions={…}>…</LeadsHeader>`;
// a page that is none of the three tabs (one lead) passes no `current`.

import { useTranslation } from '@clavian/i18n/react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Page } from '../../ui';
import { HQ_PATHS } from '../paths';
import '../hq.css';

export type LeadsTab = 'today' | 'pipeline' | 'funnel';

const TABS: readonly { readonly id: LeadsTab; readonly to: string }[] = [
  { id: 'today', to: HQ_PATHS.leads },
  { id: 'pipeline', to: HQ_PATHS.pipeline },
  { id: 'funnel', to: HQ_PATHS.funnel },
];

export function LeadsNav({ current }: { readonly current?: LeadsTab | undefined }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <nav className="console-tabs" aria-label={t('leads.sections')}>
      <ul>
        {TABS.map((tab) => (
          <li key={tab.id}>
            <Link to={tab.to} activeOptions={{ exact: true }} aria-current={current === tab.id ? 'page' : undefined}>
              {t(`leads.tab.${tab.id}`)}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export interface LeadsHeaderProps {
  readonly current?: LeadsTab | undefined;
  /** The page's actions, beside the title. */
  readonly actions?: ReactNode;
  readonly width?: 'default' | 'wide' | 'narrow';
  readonly children?: ReactNode;
}

export function LeadsHeader({ current, actions, width = 'wide', children }: LeadsHeaderProps): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Page title={t('leads.title')} tip={t('leads.intro')} actions={actions} width={width}>
      <LeadsNav current={current} />
      {children}
    </Page>
  );
}
